"use client";
import React from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { settings } from "@/lib/api/api";
import { getUniqueId } from "@gettactic/helpers/src";
import { useAuthenticated, useTacticHooks } from "@gettactic/hooks";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import { getHeaderSlug } from "@gettactic/api/src/endpoints/base";

export interface UseTessaChatOptions {
  selectedChatId?: string | null;
}

export const useTessaChat = (options?: UseTessaChatOptions) => {
  const {
    userContext: { authenticatedUser, deviceTz }
  } = useAuthenticated();

  const { queryClient } = useTacticHooks();
  const askTessa = useAskTessa();

  const refetchChats = React.useCallback(() => {
    queryClient.refetchQueries({ queryKey: ["tessa.chats"] });
  }, [queryClient]);

  const { selectedChatId } = options || {};

  const [_selectedChatId, _setSelectedChatId] = React.useState<string | null>();

  React.useEffect(() => {
    if (!selectedChatId || selectedChatId === "new") {
      _setSelectedChatId(getUniqueId());
      refetchChats();
      return;
    }

    _setSelectedChatId(selectedChatId);
    refetchChats();
  }, [selectedChatId]);

  const chat = useChat({
    ...(_selectedChatId ? { id: _selectedChatId } : {}),
    transport: new DefaultChatTransport({
      api: `${settings.url}/api/v1/tessa/chat/streaming`,
      headers: {
        "Content-Type": "application/json",
        "X-Tactic-Orgz": getHeaderSlug(authenticatedUser.organization?.slug || ""),
        "X-Tactic-Cors-Bypass": settings.corsBypass
      },
      credentials: "include",
      // only send the last message to the backend
      prepareSendMessagesRequest({ messages, id }) {
        return {
          body: {
            id: id,
            message: messages[messages.length - 1],
            user_tz: deviceTz
          }
        };
      }
    }),
    generateId: () => getUniqueId(),
    onFinish: () => {
      refetchChats();
    }
  });

  const { messages } = chat;

  // 🔥 usare useRef invece di useState
  const firstChunkHandledRef = React.useRef(false);

  React.useEffect(() => {
    firstChunkHandledRef.current = false;
  }, [_selectedChatId]);

  React.useEffect(() => {
    if (firstChunkHandledRef.current) return;

    const assistantMsg = messages.find((m) => m.role === "assistant");
    if (!assistantMsg) return;

    const hasText =
      assistantMsg.parts?.some(
        (p) =>
          p.type === "text" && typeof p.text === "string" && p.text.length > 0
      ) ?? false;

    if (!hasText) return;

    refetchChats();
    firstChunkHandledRef.current = true;
  }, [messages, refetchChats]);

  return chat;
};
